import { createReducer, on } from '@ngrx/store';
import {
    isIdemSale,
    updateChild,
} from 'shared/ui/tables/table-grouped-by-category/entityWithChildrenUtils';
import * as TotalGrossActions from '../total-gross/total-gross.actions';
import * as TerminalSalesActions from './terminal-sales.actions';
import { TerminalSale } from './terminal-sales.models';

// ----------------------------------------------------------------------
// @ States
// ----------------------------------------------------------------------

export interface TerminalSalesState {
    terminalSales: TerminalSale[];
    terminalSale: TerminalSale | null;
    isLoading: boolean;
    error: string | null;
}

// ----------------------------------------------------------------------
// @ Initial States
// ----------------------------------------------------------------------

export const initialTerminalSalesState: TerminalSalesState = {
    terminalSales: [],
    terminalSale: null,
    isLoading: false,
    error: null,
};

// ----------------------------------------------------------------------
// @ Reducers
// ----------------------------------------------------------------------

// All TerminalSales Reducer
export const terminalSalesReducer = createReducer(
    initialTerminalSalesState,

    // On Load TerminalSales
    on(TerminalSalesActions.loadTerminalSales, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        TerminalSalesActions.loadTerminalSalesSuccess,
        (state, { terminalSales }) => ({
            ...state,
            isLoading: false,
            terminalSales: terminalSales,
        })
    ),
    on(TerminalSalesActions.loadTerminalSalesFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Clear Terminal Sales
    on(TerminalSalesActions.clearTerminalSales, (state) => ({
        ...state,
        terminalSales: [],
        terminalSale: null,
        isLoading: false,
        error: null,
    })),

    // On Load Single TerminalSale
    on(TerminalSalesActions.loadTerminalSale, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        TerminalSalesActions.loadTerminalSaleSuccess,
        (state, { terminalSale }) => ({
            ...state,
            isLoading: false,
            terminalSale: terminalSale,
        })
    ),
    on(TerminalSalesActions.loadTerminalSaleFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Create
    on(TerminalSalesActions.createTerminalSale, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        TerminalSalesActions.createTerminalSaleSuccess,
        (state, { terminalSale }) => ({
            ...state,
            isLoading: false,
            terminalSales: ((): TerminalSale[] => {
                const sales = state.terminalSales || [];
                let updateSales = [];
                sales.some(
                    (sale) => sale.location.id === terminalSale.location.id
                )
                    ? (updateSales = sales.map((sale: TerminalSale) =>
                          sale.location.id === terminalSale.location.id
                              ? {
                                    // Spread the old sale to keep old cash
                                    // and credit card
                                    ...sale,
                                    children: [
                                        ...sale.children,
                                        ...terminalSale.children,
                                    ],
                                }
                              : sale
                      ))
                    : (updateSales = [...sales, terminalSale]);

                return updateSales;
            })(),
        })
    ),
    on(TerminalSalesActions.createTerminalSaleFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Update
    on(TerminalSalesActions.updateTerminalSale, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        TerminalSalesActions.updateTerminalSaleSuccess,
        (state, { terminalSale }) => ({
            ...state,
            isLoading: false,
            terminalSales: state.terminalSales.map((sale: TerminalSale) =>
                isIdemSale(sale, terminalSale)
                    ? {
                          ...terminalSale,
                          children: updateChild(sale, terminalSale),
                      }
                    : sale
            ),
        })
    ),
    on(TerminalSalesActions.updateTerminalSaleFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Delete Sale
    on(TerminalSalesActions.deleteTerminalSale, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        TerminalSalesActions.deleteTerminalSaleSuccess,
        (state, { deletedSales }) => ({
            ...state,
            isLoading: false,
            terminalSales: state.terminalSales.filter((sales: TerminalSale) =>
                sales.children.every(
                    (child) => child.saleId !== deletedSales[0]
                )
            ),
        })
    ),
    on(TerminalSalesActions.deleteTerminalSaleFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Delete Sale Item
    on(TerminalSalesActions.deleteTerminalSaleItem, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        TerminalSalesActions.deleteTerminalSaleItemSuccess,
        (state, { deletedSaleItem }) => ({
            ...state,
            isLoading: false,
            terminalSales: state.terminalSales.map((sales: TerminalSale) => {
                if (
                    sales.children.some(
                        (child) => child.saleId === deletedSaleItem
                    )
                ) {
                    return {
                        ...sales,
                        children: sales.children.filter(
                            (child) => child.saleId !== deletedSaleItem
                        ),
                    };
                } else {
                    return sales;
                }
            }),
        })
    ),
    on(
        TerminalSalesActions.deleteTerminalSaleItemFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error: error,
        })
    ),

    // On update total gross
    on(
        TotalGrossActions.updateTotalGrossByLocationSuccess,
        (state, { totalGross }) => ({
            ...state,
            isLoading: false,
            terminalSales: state.terminalSales.map((sales: TerminalSale) =>
                sales.location.id === totalGross.location_id
                    ? {
                          ...sales,
                          cash: totalGross.cash,
                          credit_card: totalGross.credit_card,
                      }
                    : sales
            ),
        })
    )
);
